import type { ReactElement } from 'react';
import { Trans } from 'react-i18next';

import type { PixieAnswer, PixieData } from '@/features/pixie/types';
import { mandatoryMriCount } from '@/shared/domain/mri';
import { byQueue } from '@/shared/domain/selectors';
import type { CaseRecord } from '@/shared/domain/case';
import { orderTypes } from '@/shared/domain/taxonomy';

/* Pixie's explanations. Three rules hold this file together, and they are
   enforced by construction rather than asked for in a prompt:
     1. Grounded — every answer names the case data it was read from.
     2. Proposes — it drafts and explains; it cannot send, sign off or order.
     3. Scoped   — it only sees the case PixieData was built with.

   Each function takes the resolved data bundle and returns an answer, so an
   answer can never reach past what the panel was allowed to load. The words come
   from the same bundle, for the same reason: an answer is not a component, so it
   is handed its translator rather than reaching for a hook.

   The `intents` table at the bottom is a keyword list, not prose — see the note
   in `skills.tsx` for why the German stems sit outside the `\b(…)\b` groups and
   why a suggestion chip and the pattern that catches it move together. */

type Answer = (data: PixieData) => PixieAnswer;

function Mono({ children }: { readonly children: string | undefined }): ReactElement {
  return <code>{children ?? ''}</code>;
}

/** How many mandatory fields are still open on the scoped case. */
function openFields(data: PixieData): readonly string[] {
  return data.detail?.missing ?? [];
}

function summary(data: PixieData): PixieAnswer {
  const { t } = data;
  const record = data.caseRecord;
  if (!record) return { text: t('pixie:answers.summaryNoCase') };

  const type = orderTypes[record.type];
  const missing = openFields(data);
  return {
    text: (
      <>
        <Trans
          ns="pixie"
          i18nKey="answers.summary"
          values={{
            customer: data.vocabulary.caseCustomer(record),
            offering: record.offering,
            type: data.vocabulary.orderType(record.type),
            action: type.action,
            category: type.category,
            state: data.vocabulary.caseState(record),
          }}
          components={{ b: <b />, mono: <code /> }}
        />{' '}
        {missing.length > 0
          ? t('pixie:answers.summaryOpen', { count: missing.length })
          : t('pixie:answers.summaryComplete')}
      </>
    ),
    cites: ['classification', 'catalog match'],
    actions:
      missing.length > 0
        ? [
            {
              kind: 'ask',
              label: t('pixie:actions.showMissing'),
              question: t('pixie:probes.whatIsMissing'),
            },
          ]
        : [{ kind: 'open', label: t('pixie:actions.openMri'), to: `/case/${record.id}/mri` }],
  };
}

function gaps(data: PixieData): PixieAnswer {
  const { t } = data;
  const record = data.caseRecord;
  if (!record) return { text: t('pixie:answers.gapsNoCase') };

  const missing = openFields(data);
  if (missing.length === 0) {
    return {
      text: t('pixie:answers.gapsNone', {
        id: record.id,
        total: mandatoryMriCount(data.mriFields),
      }),
      cites: ['attachment scan'],
    };
  }

  const named = missing.map((name) => data.mriFields.find((field) => field.name === name));
  return {
    text: (
      <>
        {t('pixie:answers.gapsHead', { id: record.id, count: missing.length })}
        <ul>
          {named.map((field, index) => (
            <li key={field?.id ?? missing[index]}>
              <b>{field?.businessName ?? missing[index]}</b>{' '}
              <Mono>{field?.name ?? missing[index]}</Mono>
              {field?.validationRuleKey ? ` — ${field.validationRuleKey}` : null}
            </li>
          ))}
        </ul>
      </>
    ),
    actions: [
      {
        kind: 'ask',
        label: t('pixie:probes.draftClarification'),
        question: t('pixie:probes.draftClarification'),
      },
      { kind: 'open', label: t('pixie:actions.openGaps'), to: `/case/${record.id}/gaps` },
    ],
  };
}

function confidence(data: PixieData): PixieAnswer {
  const { t } = data;
  const record = data.caseRecord;
  if (!record) return { text: t('pixie:answers.confidenceNoCase') };

  return {
    text: (
      <>
        <Trans
          ns="pixie"
          i18nKey="answers.confidence"
          values={{ id: record.id, score: data.format.number(record.confidence, 2) }}
          components={{ b: <b /> }}
        />{' '}
        {record.disposition === 'conflict'
          ? t('pixie:answers.confidenceConflict')
          : t('pixie:answers.confidenceGeneral')}
      </>
    ),
    cites: ['classification'],
    actions: [
      {
        kind: 'ask',
        label: t('pixie:probes.whatIsMissing'),
        question: t('pixie:probes.whatIsMissing'),
      },
    ],
  };
}

function draft(data: PixieData): PixieAnswer {
  const { t } = data;
  const record = data.caseRecord;
  if (!record) return { text: t('pixie:answers.draftNoCase') };

  const missing = openFields(data);
  if (missing.length === 0) {
    return { text: t('pixie:answers.draftNothing', { id: record.id }) };
  }
  return {
    text: (
      <Trans
        ns="pixie"
        i18nKey="answers.draftBody"
        count={missing.length}
        values={{ count: missing.length }}
        components={{ b: <b /> }}
      />
    ),
    actions: [
      { kind: 'open', label: t('pixie:actions.openDraft'), to: `/case/${record.id}/communication` },
    ],
  };
}

function catalog(data: PixieData): PixieAnswer {
  const { t } = data;
  const record = data.caseRecord;
  const mail = record ? data.mails.find((entry) => entry.case === record.id) : undefined;
  if (!record || !mail) {
    return {
      text: (
        <Trans ns="pixie" i18nKey="answers.catalogGeneral" components={{ mono: <code /> }} />
      ),
    };
  }
  return {
    text: (
      <>
        <Trans
          ns="pixie"
          i18nKey="answers.catalogCheck"
          values={{ id: record.id, note: data.vocabulary.catalogNote(mail.catalog) }}
          components={{ b: <b /> }}
        />{' '}
        {mail.catalog.state === 'matched'
          ? t('pixie:answers.catalogMatched')
          : t('pixie:answers.catalogHeld')}
      </>
    ),
    cites: ['catalog match'],
    actions: [
      { kind: 'open', label: t('pixie:actions.openRouting'), to: `/case/${record.id}/routing` },
    ],
  };
}

function routing(data: PixieData): PixieAnswer {
  const { t } = data;
  const record = data.caseRecord;
  if (!record) return { text: t('pixie:answers.routingGeneral') };

  const type = orderTypes[record.type];
  return {
    text: (
      <Trans
        ns="pixie"
        i18nKey={
          record.queue === 'work' ? 'answers.routingWork' : 'answers.routingHeld'
        }
        values={{
          id: record.id,
          queue: data.vocabulary.queue(record.queue),
          action: type.action,
          category: type.category,
        }}
        components={{ b: <b />, mono: <code /> }}
      />
    ),
    cites: ['classification'],
    actions: [
      { kind: 'open', label: t('pixie:actions.openRouting'), to: `/case/${record.id}/routing` },
    ],
  };
}

function evidence(data: PixieData): PixieAnswer {
  const { t } = data;
  const record = data.caseRecord;
  if (!record) return { text: t('pixie:answers.evidenceNoCase') };

  const sources = record.sources;
  return {
    text:
      sources.length > 0
        ? t('pixie:answers.evidenceBody', { id: record.id })
        : t('pixie:answers.evidenceNoSources', { id: record.id }),
    /* The cite strings are keys into `evidence.data`, not prose: each one
       indexes the span the modal opens. They come from the case record itself
       so a new source type never requires a code change here. */
    ...(sources.length > 0 ? { cites: sources } : {}),
  };
}

function nextAction(data: PixieData): PixieAnswer {
  const { t } = data;
  const record = data.caseRecord;
  if (!record) {
    const work = byQueue(data.cases, 'work');
    const conflict = data.cases.find((entry) => entry.disposition === 'conflict');
    return {
      text: conflict
        ? t('pixie:answers.nextQueueConflict', { total: work.length, id: conflict.id })
        : t('pixie:answers.nextQueueClear', { total: work.length }),
    };
  }
  return {
    text: (
      <Trans
        ns="pixie"
        i18nKey="answers.nextCase"
        values={{ id: record.id, action: data.vocabulary.caseAction(record) }}
        components={{ b: <b /> }}
      />
    ),
  };
}

/* The two boundaries. Both are capability statements rather than policy, because
   that is what they are: Pixie holds no tool that could do either. */

function refuseSend(data: PixieData): PixieAnswer {
  const { t } = data;
  const record = data.caseRecord;
  return {
    refusal: true,
    text: (
      /* The tab is quoted from `case:tabs`, not spelled again here, so the
         sentence keeps naming the tab the reader will actually see if it is
         ever renamed. `mail.create_draft` and `mail.send` are tool names and
         stay in the wire's own words. */
      <Trans
        ns="pixie"
        i18nKey="answers.refuseSend"
        values={{ tab: t('case:tabs.communication') }}
        components={{ draftTool: <code />, sendTool: <code /> }}
      />
    ),
    ...(record
      ? {
          actions: [
            {
              kind: 'open' as const,
              label: t('pixie:actions.openDraftForApproval'),
              to: `/case/${record.id}/communication`,
            },
          ],
        }
      : {}),
  };
}

function refuseCommit(data: PixieData): PixieAnswer {
  const { t } = data;
  const record = data.caseRecord;
  return {
    refusal: true,
    text: t('pixie:answers.refuseCommit'),
    ...(record
      ? {
          actions: [
            {
              kind: 'open' as const,
              label: t('pixie:actions.openMriForReview'),
              to: `/case/${record.id}/mri`,
            },
          ],
        }
      : {}),
  };
}

/* Stems are matched without a trailing boundary so "confidence" hits "confiden".
   Order matters: the two refusals are tested first, so no phrasing of "send it"
   can be answered as a drafting question. */
const intents: readonly (readonly [RegExp, Answer])[] = [
  [
    /\b(send|dispatch|mail it|email them now|reply now)\b|senden|versende|abschicke|jetzt antworten/i,
    refuseSend,
  ],
  [
    /(sign.?off|sign it off|approve it|create the order|submit the order|raise the order)|freigeben|unterschreib|auftrag anlegen|bestellung auslösen|abzeichnen/i,
    refuseCommit,
  ],
  [
    /(missing|gap|incomplete|outstanding|what.*(need|require))|fehlt|fehlend|lücke|unvollständig|offene felder/i,
    gaps,
  ],
  [/(confiden|score|how sure|why.*0\.)|konfidenz|wie sicher|punktzahl/i, confidence],
  [/(draft|clarif|write|compose|ask the customer)|entwerf|entwurf|rückfrage|formulier/i, draft],
  [/(catalog|offering|orderable|launched)|katalog|angebot|bestellbar|eingeführt/i, catalog],
  [/(queue|rout|macd|classif|why is it here)|vorrat|weiterleit|klassifiz|warum liegt/i, routing],
  [/(source|evidence|provenance|where.*from|proof)|quelle|beleg|herkunft|woher/i, evidence],
  [/(next|what should i|recommend|action)|nächst|empfehl|was soll ich/i, nextAction],
  [/(summar|overview|brief|what is this|explain the case)|zusammenfass|übersicht|erkläre/i, summary],
];

function fallback(data: PixieData): PixieAnswer {
  const { t } = data;
  return {
    text: t('pixie:answers.fallback'),
    actions: [
      { kind: 'ask', label: t('pixie:probes.summarise'), question: t('pixie:probes.summarise') },
      {
        kind: 'ask',
        label: t('pixie:probes.whatIsMissing'),
        question: t('pixie:probes.whatIsMissing'),
      },
    ],
  };
}

export function explain(question: string, data: PixieData): PixieAnswer {
  for (const [pattern, answer] of intents) {
    if (pattern.test(question)) return answer(data);
  }
  return fallback(data);
}

/** The opening message. It states the scope and the boundary before the first
    question is asked, rather than waiting to be caught out by one. */
export function greeting(data: PixieData): PixieAnswer {
  const record = data.caseRecord;
  if (!record) return { text: data.t('pixie:answers.greetingQueue') };

  return {
    text: (
      <Trans
        ns="pixie"
        i18nKey="answers.greetingCase"
        values={{ id: record.id }}
        components={{ b: <b /> }}
      />
    ),
  };
}

/** Chips offered under the log — the questions worth asking from where you are.
 *
 * A chip's `question` is not a label: it is echoed into the log as if the
 * operator had typed it, and then run through the same matcher. So it is a
 * catalogue string in both languages, and each one has to stay within the
 * vocabulary `skills.tsx` and `intents` listen for. */
export function suggestionsFor(data: PixieData): readonly { label: string; question: string }[] {
  const { t } = data;
  const record: CaseRecord | null = data.caseRecord;
  if (!record) {
    const chips: { label: string; question: string }[] = [
      { label: t('pixie:chips.needsDecision'), question: t('pixie:probes.needsDecision') },
    ];
    /* Use the first real case in scope so the chip always resolves. The
       hardcoded CLC-104882 never existed in the tenant and always failed. */
    const firstCase = data.cases[0];
    if (firstCase) {
      const openQ = t('pixie:probes.openCase', { id: firstCase.id });
      chips.push({ label: openQ, question: openQ });
    }
    chips.push({ label: t('pixie:probes.analytics'), question: t('pixie:probes.analytics') });
    return chips;
  }
  const chips = [{ label: t('pixie:probes.summarise'), question: t('pixie:probes.summarise') }];
  if (openFields(data).length > 0) {
    chips.push(
      { label: t('pixie:probes.whatIsMissing'), question: t('pixie:probes.whatIsMissing') },
      {
        label: t('pixie:probes.draftClarification'),
        question: t('pixie:probes.draftClarification'),
      },
    );
  }
  chips.push({ label: t('pixie:chips.confidence'), question: t('pixie:probes.confidence') });
  if (record.queue !== 'work') {
    chips.push({ label: t('pixie:chips.queue'), question: t('pixie:probes.queue') });
  }
  chips.push(
    {
      label: t('pixie:chips.customer'),
      question: t('pixie:probes.customer', { customer: data.vocabulary.caseCustomer(record) }),
    },
    // Kept deliberately: the boundary is easier to trust once you have seen it hold.
    { label: t('pixie:chips.send'), question: t('pixie:probes.send') },
  );
  return chips;
}
